// After-hours warning — shows when pick-up and/or return fall outside
// operating hours. Supports per-listing overrides (KAN-149) or the
// legacy 22:00–06:00 / $25 defaults.

import React from "react";
import { Moon } from "lucide-react";
import { parseDateTimeLocal } from "../lib/dateLocal";

const QUIET_START = 22;   // 10 PM
const QUIET_END = 6;      // 6 AM
const LATE_FEE_USD = 25;

/**
 * Check whether a datetime falls outside operating hours.
 * Open and close boundaries are inclusive (matches time-slot picker).
 * @param {string} when — "YYYY-MM-DDTHH:MM"
 * @param {string} [operatingOpen] — "HH:MM" per-listing open
 * @param {string} [operatingClose] — "HH:MM" per-listing close
 */
export function isAfterHoursSlot(when, operatingOpen, operatingClose) {
  const d = parseDateTimeLocal(when);
  if (!d) return false;
  const hr = d.getHours() + d.getMinutes() / 60;

  if (operatingOpen && operatingClose) {
    const [oh, om] = operatingOpen.split(":").map(Number);
    const [ch, cm] = operatingClose.split(":").map(Number);
    const openDec = oh + om / 60;
    const closeDec = ch + cm / 60;
    if (openDec <= closeDec) {
      return hr < openDec || hr > closeDec;
    }
    return hr > closeDec && hr < openDec;
  }

  return hr >= QUIET_START || hr < QUIET_END;
}

/** @deprecated Prefer isAfterHoursSlot */
export function isAfterHoursReturn(returnAt) {
  return isAfterHoursSlot(returnAt);
}

function formatHoursWindow(operatingOpen, operatingClose) {
  if (operatingOpen && operatingClose) return `${operatingOpen} – ${operatingClose}`;
  return "22:00 – 06:00";
}

/**
 * @param {string} [pickupAt] — "YYYY-MM-DDTHH:MM" pick-up datetime
 * @param {string} [returnAt] — "YYYY-MM-DDTHH:MM" return datetime
 * @param {string} [operatingOpen] — per-listing operating open "HH:MM"
 * @param {string} [operatingClose] — per-listing operating close "HH:MM"
 * @param {number} [afterHoursFeeAmount] — per-listing fee override
 */
export default function LateReturnWarning({
  pickupAt, returnAt,
  operatingOpen, operatingClose,
  afterHoursFeeAmount,
}) {
  const feePerSlot = afterHoursFeeAmount != null ? afterHoursFeeAmount : LATE_FEE_USD;
  const pickupQuiet = pickupAt ? isAfterHoursSlot(pickupAt, operatingOpen, operatingClose) : false;
  const returnQuiet = returnAt ? isAfterHoursSlot(returnAt, operatingOpen, operatingClose) : false;
  if (!pickupQuiet && !returnQuiet) return null;

  const slots = [];
  if (pickupQuiet) slots.push("pick-up");
  if (returnQuiet) slots.push("return");
  const slotLabel = slots.join(" and ");
  const feeTotal = feePerSlot * slots.length;
  const window = formatHoursWindow(operatingOpen, operatingClose);

  const animKey = "zcLatePulse";
  return (
    <div
      className="mt-3 flex items-start gap-3 px-4 py-3 rounded-lg border"
      style={{
        background: "rgba(245,158,11,0.08)",
        borderColor: "rgba(245,158,11,0.45)",
        animation: `${animKey} 2.4s ease-in-out infinite`,
      }}
      data-testid="late-return-warning"
    >
      <style>{`@keyframes ${animKey} { 0%,100%{ box-shadow:0 0 0 0 rgba(245,158,11,0.0);} 50%{ box-shadow:0 0 0 6px rgba(245,158,11,0.10);} }`}</style>
      <Moon size={16} className="text-[var(--zc-warn)] shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-[var(--zc-warn)]" style={{ letterSpacing: 0.2 }}>
          After-hours {slotLabel} notice
        </div>
        <div className="text-xs mt-0.5" style={{ color: "var(--zc-text-2)" }}>
          {slotLabel.charAt(0).toUpperCase() + slotLabel.slice(1)} outside operating hours ({window})
          {slots.length > 1 ? " each include" : " includes"} a{" "}
          <b>${feePerSlot} after-hours fee</b>
          {slots.length > 1 ? ` ($${feeTotal} total)` : ""} in your checkout total when you book those times.
          Mileage beyond the included allowance is charged at trip completion.
        </div>
      </div>
    </div>
  );
}

export const LATE_RETURN_FEE = LATE_FEE_USD;
export const AFTER_HOURS_FEE = LATE_FEE_USD;
